import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Lock, Smile } from "lucide-react";
import {
  getToddlerMemberId,
  setToddlerMemberId,
  clearToddlerMode,
} from "@/lib/toddlerMode";

// Standalone sheet for toddler-mode setup. Opened from the main Settings
// panel via its own row. Gives the feature enough space to explain what
// happens when you flip it on, and keeps the settings page uncluttered.
export default function ToddlerModeSheet({ onClose }) {
  const [members, setMembers] = useState([]);
  const [active, setActive] = useState(() => getToddlerMemberId());

  useEffect(() => {
    api.listMembers().then(setMembers).catch(() => setMembers([]));
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const enable = (memberId) => {
    setToddlerMemberId(memberId);
    setActive(memberId);
    toast.success("Toddler mode on — reopening dashboard");
    // Hard reload so the dashboard picks up the locked state immediately and
    // closes every open drawer in one motion. A parent unlocks from the
    // small lock icon in the header using the family PIN.
    setTimeout(() => {
      window.location.href = "/app";
    }, 450);
  };

  const disable = () => {
    clearToddlerMode();
    setActive(null);
    toast.success("Toddler mode off");
  };

  const activeMember = active ? members.find((m) => m.id === active) : null;
  const pickable = members.filter((m) => m.role !== "pet");

  return (
    <div
      className="fixed inset-0 z-40 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="toddler-mode-sheet"
    >
      <div
        className="relative bg-[color:var(--bg)] md:rounded-[20px] w-full md:max-w-2xl max-h-[100vh] md:max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[color:var(--bg)] px-5 md:px-7 pt-5 pb-3 flex items-center justify-between border-b border-[color:var(--line-soft)] z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color:var(--bg-soft)]">
              <Smile className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
              <div className="label-tiny">Device mode</div>
              <h2 className="font-display-italic text-3xl md:text-4xl leading-none mt-0.5 tracking-tight">
                Toddler mode
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
            data-testid="toddler-mode-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="px-5 md:px-7 py-6 space-y-6">
          <p className="text-[15px] leading-snug text-[color:var(--ink-2)] max-w-lg">
            Turn a shared phone or iPad into a "just for the little one" device
            — the header icons hide, other family cards stop responding to
            taps, and a big "tap for a surprise" row appears with butterflies,
            bubbles, bouncing balls and fish to pop. Your toddler can play and
            log their own check-in without wandering into settings, billing or
            the calendar.
          </p>

          <ul className="text-[13px] leading-snug text-[color:var(--ink-2)] space-y-1.5 list-disc pl-5 max-w-lg">
            <li>Only the pinned kid's card is tappable for check-in.</li>
            <li>All other cards stay visible but ignore taps.</li>
            <li>A small <Lock className="inline w-3.5 h-3.5 -mt-0.5" strokeWidth={2} /> icon in the corner unlocks it with the family PIN.</li>
            <li>Per-device: Dad's phone stays normal while the kitchen iPad is kid-proofed.</li>
          </ul>

          {active ? (
            <div className="ff-card p-5">
              <div className="label-tiny mb-2">Currently locked to</div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">
                  {activeMember?.avatar_emoji || "🔒"}
                </span>
                <div className="font-display text-2xl">
                  {activeMember?.name || "this device"}
                </div>
              </div>
              <button
                type="button"
                onClick={disable}
                className="ff-btn"
                data-testid="toddler-mode-off"
              >
                Turn toddler mode off
              </button>
            </div>
          ) : (
            <div>
              <div className="label-tiny mb-3">Who is this device for?</div>
              {pickable.length === 0 ? (
                <p className="label-tiny text-[color:var(--ink-3)]">
                  Add a family member first.
                </p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {pickable.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => enable(m.id)}
                      className="ff-card ff-card-hover p-4 flex flex-col items-center gap-2"
                      data-testid={`toddler-mode-pick-${m.id}`}
                    >
                      <span className="text-5xl">{m.avatar_emoji}</span>
                      <span className="font-display text-lg leading-none">
                        {m.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
